
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

// Import types and utilities from the refactored modules
import { 
  FeatureId, 
  GPTModel, 
  FeatureModelConfig, 
  FeatureModelConfigMap,
  FeatureModelContextType 
} from './feature-model/types';
import { defaultConfig } from './feature-model/defaults';
import { 
  parseConfigFromStorage, 
  saveConfigToStorage,
  verifyStoredConfig 
} from './feature-model/storage-utils';
import {
  getFeatureEnabled,
  getFeatureModel,
  getFeatureApiKey,
  hasFeatureReachedUsageLimit,
  trackFeatureTokenUsage,
  resetAllFeatureUsageCounts
} from './feature-model/feature-utils';

// Re-export types for consumers
export type { 
  FeatureId, 
  GPTModel, 
  FeatureModelConfig, 
  FeatureModelConfigMap 
};

const FeatureModelContext = createContext<FeatureModelContextType | undefined>(undefined);

export const FeatureModelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [configs, setConfigs] = useState<FeatureModelConfigMap>(() => {
    return parseConfigFromStorage();
  });

  // Persistent save effect
  useEffect(() => {
    saveConfigToStorage(configs);
  }, [configs]);

  // Debug check - verify we're storing what we think we are
  useEffect(() => {
    verifyStoredConfig();
  }, [configs]);

  const updateFeatureConfig = (
    featureId: FeatureId, 
    updates: Partial<FeatureModelConfig>
  ) => {
    setConfigs(prevConfigs => {
      const newConfigs = {
        ...prevConfigs,
        [featureId]: {
          ...prevConfigs[featureId],
          ...updates
        }
      };
      
      // If updating the API key, show a confirmation toast
      if (updates.apiKey !== undefined && updates.apiKey !== prevConfigs[featureId].apiKey) {
        if (updates.apiKey) {
          toast.success(`API key updated for ${featureId}`);
        } else {
          toast.info(`API key removed for ${featureId}`);
        }
      }
      
      return newConfigs;
    });
  };

  const resetFeatureConfig = (featureId: FeatureId) => {
    setConfigs(prevConfigs => ({
      ...prevConfigs,
      [featureId]: {
        ...defaultConfig[featureId],
        apiKey: prevConfigs[featureId].apiKey // Keep the API key when resetting other settings
      }
    }));
  };

  const isFeatureEnabled = (featureId: FeatureId): boolean => {
    return getFeatureEnabled(configs, featureId);
  };

  const getModelForFeature = (featureId: FeatureId): GPTModel => {
    return getFeatureModel(configs, featureId);
  };
  
  const getApiKeyForFeature = (featureId: FeatureId): string => {
    return getFeatureApiKey(configs, featureId);
  };
  
  const trackTokenUsage = (featureId: FeatureId, tokenCount: number) => {
    setConfigs(prevConfigs => trackFeatureTokenUsage(prevConfigs, featureId, tokenCount));
  };
  
  const hasReachedUsageLimit = (featureId: FeatureId): boolean => {
    return hasFeatureReachedUsageLimit(configs, featureId);
  };
  
  const resetAllUsageCounts = () => {
    setConfigs(prevConfigs => resetAllFeatureUsageCounts(prevConfigs));
  };

  return (
    <FeatureModelContext.Provider value={{
      configs,
      updateFeatureConfig,
      resetFeatureConfig,
      isFeatureEnabled,
      getModelForFeature,
      getApiKeyForFeature,
      trackTokenUsage,
      hasReachedUsageLimit,
      resetAllUsageCounts
    }}>
      {children}
    </FeatureModelContext.Provider>
  );
};

export const useFeatureModel = (): FeatureModelContextType => {
  const context = useContext(FeatureModelContext);
  if (!context) {
    throw new Error('useFeatureModel must be used within a FeatureModelProvider');
  }
  return context;
};
